import { apiClient } from "@/lib/api/http"

// ── Types ───────────────────────────────────────────────────────────────────────
export interface BuyerProfile {
  id: string
  userId: string
  phoneNumber: string
  addressLine1: string
  addressLine2: string | null
  city: string
  state: string
  postalCode: string
  country: string
  preferences: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateBuyerProfilePayload {
  phoneNumber: string | null
  addressLine1: string | null
  addressLine2?: string | null
  city: string | null
  state: string | null
  postalCode: string | null
  country: string | null
  preferences?: string | null
}

// ── Envelope wrapper (backend wraps all GET/POST responses) ──────────────────────────
interface Envelope<T> {
  success: boolean
  statusCode: number
  message: string
  data: T
}

// ── GET /buyer/{id} — full profile for display ────────────────────────────────
export async function getBuyerProfileById(
  id: string,
  token: string
): Promise<BuyerProfile> {
  const { data: envelope } = await apiClient.get<Envelope<BuyerProfile>>(
    `/buyer/${id}`,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

// ── POST /buyer ────────────────────────────────────────────────────────────────
export async function createBuyerProfile(
  payload: CreateBuyerProfilePayload,
  token: string
): Promise<BuyerProfile> {
  const { data: envelope } = await apiClient.post<Envelope<BuyerProfile>>(
    "/buyer",
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

// ── PATCH /buyer/{id} ──────────────────────────────────────────────────────────
export interface UpdateBuyerProfilePayload {
  phoneNumber?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
  preferences?: string
}

export async function updateBuyerProfile(
  id: string,
  payload: UpdateBuyerProfilePayload,
  token: string
): Promise<BuyerProfile> {
  const { data: envelope } = await apiClient.patch<Envelope<BuyerProfile>>(
    `/buyer/${id}`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

export async function searchBuyers(name: string, token: string, signal?: AbortSignal): Promise<any[]> {
  const { data: envelope } = await apiClient.get<Envelope<any[]>>(
    "/buyer/search",
    {
      headers: { Authorization: `Bearer ${token}` },
      params: { name },
      signal,
    }
  )
  return envelope.data
}

export interface BuyerAddressPayload {
  title: string
  addressLine1: string
  addressLine2?: string
  city: string
  cityId: string
  state: string
  postalCode: string
  country: string
  isDefault: boolean
}

export async function addBuyerAddress(
  payload: BuyerAddressPayload,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    "/buyer/address",
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

export async function getBuyerAddresses(
  token: string
): Promise<any[]> {
  const { data: envelope } = await apiClient.get<Envelope<any[]>>(
    "/buyer/address",
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

export async function setBuyerDefaultAddress(
  addressId: string,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.patch<Envelope<any>>(
    `/buyer/address/${addressId}/default`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

// ── GET /analytics/buyer/kpis ────────────────────────────────────────────────
export interface BuyerKpiMetric {
  value: number
  previousValue?: number
  formattedValue?: string
  currency?: string
  trend: string
  trendPercentage: number
  trendDirection: "up" | "down" | "neutral"
  sparkline: number[]
  breakdown?: {
    pendingQuotations?: number
    pendingPayments?: number
    [key: string]: any
  }
}

export interface BuyerKpiTimeframeData {
  periodLabel: string
  kpis: {
    activeQuotations: BuyerKpiMetric
    activeOrders: BuyerKpiMetric
    awaitingAction: BuyerKpiMetric
    confirmedValue: BuyerKpiMetric
    [key: string]: any
  }
}

export interface BuyerKpisResponse {
  timeframes: Record<string, BuyerKpiTimeframeData>
}

export async function getBuyerKpis(
  buyerId: string,
  token: string
): Promise<BuyerKpisResponse> {
  const { data: envelope } = await apiClient.get<Envelope<BuyerKpisResponse>>(
    "/analytics/buyer/kpis",
    {
      params: { buyerId },
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

// ── GET /analytics/buyer/business-activity ────────────────────────────────────
export interface BusinessActivityChartPoint {
  date: string
  timestamp?: string
  rfqsCreated: number
  quotationsReceived: number
  ordersPlaced: number
}

export interface BusinessActivityTimeframeSummary {
  totalRfqs: number
  totalQuotations: number
  totalOrders: number
  conversionRate: string
}

export interface BusinessActivityTimeframeData {
  summary: BusinessActivityTimeframeSummary
  points: BusinessActivityChartPoint[]
}

export interface BuyerBusinessActivityResponse {
  timeframes: Record<string, BusinessActivityTimeframeData>
}

export async function getBuyerBusinessActivity(
  buyerId: string,
  token: string,
  timeframe?: string
): Promise<BuyerBusinessActivityResponse> {
  const { data: envelope } = await apiClient.get<Envelope<BuyerBusinessActivityResponse>>(
    "/analytics/buyer/business-activity",
    {
      params: {
        buyerId,
        ...(timeframe ? { timeframe } : {}),
      },
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

